import PropTypes from 'prop-types';
import React from 'react';
import isEqual from 'react-fast-compare';

import makeCancelable from 'lib/makeCancelable';
import notifications from 'notifications';

import * as FileSelectorUtil from './FileSelectorUtil';
import FolderDAO from './FolderDAO';
import FolderDAOCache from './FolderDAOCache';

/**
 * A composable wrapper component for providing a filtered set of folders to the wrapped component, e.g.:
 *
 *   <GetFoldersWrapper folderFilter={{showInventory: true}}>
 *     {(folders) => <span>{folders ? `${folders.length} folders` : 'Loading...'}</span>}}
 *   </GetFoldersWrapper>
 *
 */
class GetFoldersWrapper extends React.Component {
  static propTypes = {
    children: PropTypes.func.isRequired,
    folderFilter: PropTypes.shape({
      includeHiddenProjects: PropTypes.bool.isRequired,
      showInventory: PropTypes.bool.isRequired,
      showNotebook: PropTypes.bool.isRequired,
      showRegistries: PropTypes.bool.isRequired,
      level: PropTypes.string.isRequired,
    }),
  };
  state = {folders: null};

  componentDidMount() {
    // implements Backbone.Events indirectly through _.extend
    FolderDAOCache.on(FolderDAOCache.FOLDER_CACHE_INVALIDATE, this._handleFoldersInvalidate);
    this._fetchData(this.props.folderFilter);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const oldParams = FileSelectorUtil.folderFilterToListParams(this.props.folderFilter);
    const newParams = FileSelectorUtil.folderFilterToListParams(nextProps.folderFilter);
    if (!isEqual(oldParams, newParams)) {
      this._fetchData(nextProps.folderFilter);
    }
  }

  componentWillUnmount() {
    FolderDAOCache.off(FolderDAOCache.FOLDER_CACHE_INVALIDATE, this._handleFoldersInvalidate);
    if (this._fetchPromise) {
      this._fetchPromise.cancel();
    }
  }

  _handleFoldersInvalidate = () => {
    this._fetchData(this.props.folderFilter);
  };

  _fetchData = (folderFilter) => {
    if (this._fetchPromise) {
      this._fetchPromise.cancel();
    }
    this._fetchPromise = makeCancelable(
      Promise.resolve(FolderDAO.fetchAll(FileSelectorUtil.folderFilterToListParams(this.props.folderFilter)))
    );
    this._fetchPromise.promise
      .then((folders) => {
        const predicate = FileSelectorUtil.folderPredicate(folderFilter);
        this.setState({
          folders: folders.filter((folder) => predicate(folder)),
        });
      })
      .catch((response) => {
        if (response.isCanceled) {
          return;
        }
        notifications.notifyError(response);
      });
  };

  render() {
    return this.props.children(this.state.folders);
  }
}

export default GetFoldersWrapper;
